import { useNavigate } from "react-router-dom";
import get from "lodash/get";
import {
  DndContext,
  closestCorners,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  Over,
  useDroppable,
  TouchSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Fab,
  Badge,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { ACTIVE_COLOR, DEFAULT_COLOR, YELLOW_COLOR } from "@/constants/color";
import { useLeads } from "@/services/useLeads";
import { Kanban, Lead } from "@/types/lead";
import updateLeadStatus from "@/services/leads/updateStatus";
import { Bot, Filter as FilterIcon, Plus } from "lucide-react";
import { FilterDrawer } from "@/components/forms/Filter";
import { LeadDrawer } from "@/components/forms/Lead";
import {
  escapeRegex,
  formatWithCommas,
  normalizeNoAccents,
} from "@/utils/func";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { usePersistedFilters } from "@/hooks/usePersistedFilters";
import { BOT_URL } from "@/constants/bot";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

type Column = {
  id: string;
  title: string;
  rows: Lead[];
};

function LeadCard({ lead }: { lead: Lead }) {
  const navigate = useNavigate();
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const lastHistory = lead.history
    ? [...lead.history].sort(
        (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
      )[0]
    : null;

  const hoursDiff = lastHistory
    ? dayjs().diff(dayjs(lastHistory.createdAt), "hour", true)
    : Infinity; // si no hay history, tratamos como mucho tiempo

  let indicatorColor = "red";
  if (hoursDiff <= 24) indicatorColor = "#28a745";
  else if (hoursDiff > 24 && hoursDiff <= 48) indicatorColor = "orange";

  return (
    <div ref={setNodeRef} style={style}>
      <Paper
        onDoubleClick={() => navigate(`/leads/${lead.id}`)}
        {...attributes}
        {...listeners}
        sx={{
          p: 2,
          mb: 2,
          minWidth: 200,
          minHeight: 140,
          maxHeight: 140,
          "&:hover": { boxShadow: 4 },
          cursor: "grab",
          userSelect: "none",
          position: "relative",
        }}
      >
        <Box
          sx={{
            height: 6,
            width: "100%",
            bgcolor: indicatorColor,
            position: "absolute",
            top: 0,
            left: 0,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
        />
        <Typography variant="body2" fontWeight="bold" color={YELLOW_COLOR}>
          {lead.campaign}
        </Typography>
        <Typography fontWeight={600} sx={{ mt: 2 }}>
          {lead.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {lead.phone}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "12px", color: ACTIVE_COLOR }}
        >
          {lead.type ?? "-"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "12px", color: ACTIVE_COLOR }}
        >
          {lead.user ? `${lead.user?.name} ${lead.user?.lastName}` : "-"}
        </Typography>
        <Box sx={{ textAlign: "end", mt: 2 }}>
          <Typography variant="caption" color="text.disabled">
            {dayjs(lead.createdAt).format("DD/MM/YYYY HH:mm")}
          </Typography>
        </Box>
      </Paper>
    </div>
  );
}

function DragOverlayCard({ lead }: { lead: Lead }) {
  return (
    <Paper
      sx={{
        p: 2,
        minWidth: 200,
        opacity: 0.9,
        backgroundColor: ACTIVE_COLOR,
        color: "#fff",
        minHeight: 140,
        maxHeight: 140,
        overflowY: "auto",
      }}
    >
      <Typography variant="body2" fontWeight="bold" color={YELLOW_COLOR}>
        {lead.campaign}
      </Typography>
      <Typography fontWeight={600} sx={{ mt: 2, color: "#fff" }}>
        {lead.name}
      </Typography>
      <Typography variant="body2" color="#fff">
        {lead.phone}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontSize: "12px", color: "white" }}
      >
        {lead.type ?? "-"}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontSize: "12px", color: "white" }}
      >
        {lead.user ? `${lead.user?.name} ${lead.user?.lastName}` : "-"}
      </Typography>
      <Box sx={{ textAlign: "end", mt: 2 }}>
        <Typography variant="caption" color="#fff">
          {dayjs(lead.createdAt).format("DD/MM/YYYY HH:mm")}
        </Typography>
      </Box>
    </Paper>
  );
}

function EmptyPlaceholder({ id }: { id: string }) {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: 80,
        borderRadius: 1,
        border: "2px dashed #ccc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#999",
        fontStyle: "italic",
        mb: 2,
      }}
    >
      Vacío
    </Box>
  );
}

function DroppableColumn({
  column,
  isOver,
  children,
}: {
  column: Column;
  isOver: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: column.id });
  return (
    <Box
      ref={setNodeRef}
      id={column.id}
      sx={{
        minWidth: 300,
        maxHeight: "100vh",
        overflowY: "auto",
        flexShrink: 0,
        bgcolor: "#f4f5f7",
        borderRadius: 2,
        p: 2,
        transition: "border 0.2s ease",
      }}
    >
      <Badge
        badgeContent={formatWithCommas(column.rows?.length || 0)}
        max={9999}
        sx={{
          width: "100%",
          "& .MuiBadge-badge": {
            right: 8,
            top: 8,
            color: "#fff",
            backgroundColor: YELLOW_COLOR,
          },
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: "#fff",
            backgroundColor: ACTIVE_COLOR,
            p: 0.5,
            borderRadius: 2,
            textAlign: "center",
            width: "100%",
          }}
        >
          {column.title}
        </Typography>
      </Badge>

      {children}
    </Box>
  );
}

export default function HorizontalKanban() {
  const { data, isLoading } = useLeads();
  const [columns, setColumns] = useState<Kanban[]>([]);
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  const [overColId, setOverColId] = useState<string | null>(null);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const { filters, setFilters, resetFilters } = usePersistedFilters(
    undefined,
    "leads-filter"
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  // Detecta si es dispositivo táctil
  const isTouch = "ontouchstart" in window;

  const sensors = useSensors(
    isTouch
      ? useSensor(TouchSensor, {
          activationConstraint: {
            delay: 500, // 2 segundos
            tolerance: 5, // mínima distancia de movimiento antes de activar
          },
        })
      : useSensor(PointerSensor, {
          activationConstraint: {
            delay: 500, // 2 segundos también en desktop
            tolerance: 5,
          },
        })
  );

  useEffect(() => {
    if (data) {
      setColumns(data);
    }
  }, [data]);

  useEffect(() => {
    if (!data) return;

    const {
      projectSelected,
      userSelected,
      name,
      startDate,
      endDate,
      type,
      region,
      cordination,
      principal,
      master,
      color,
    } = filters;

    let filtered = data;

    if (color) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter((lead) => {
          const lastHistory = lead.history
            ? [...lead.history].sort(
                (a, b) =>
                  dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
              )[0]
            : null;

          const hoursDiff = lastHistory
            ? dayjs().diff(dayjs(lastHistory.createdAt), "hour", true)
            : Infinity;

          let indicatorColor = "red";
          if (hoursDiff <= 24) indicatorColor = "green";
          else if (hoursDiff > 24 && hoursDiff <= 48) indicatorColor = "orange";
          return indicatorColor === color;
        }),
      }));
    }

    if (projectSelected) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter((lead) => lead.campaign === projectSelected),
      }));
    }

    if (userSelected) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter(
          (lead) =>
            lead.user &&
            `${get(lead, "user.name")} ${get(lead, "user.lastName")}` ===
              userSelected
        ),
      }));
    }

    if (name) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter((deal) => {
          const dealName = normalizeNoAccents(deal.name).toLowerCase();
          const search = normalizeNoAccents(name).toLowerCase();
          return new RegExp(escapeRegex(search), "i").test(dealName);
        }),
      }));
    }

    if (startDate || endDate) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter((lead) =>
          lead.history?.some(
            (h) =>
              (!startDate ||
                dayjs(h.createdAt).isSameOrAfter(dayjs(startDate), "day")) &&
              (!endDate ||
                dayjs(h.createdAt).isSameOrBefore(dayjs(endDate), "day"))
          )
        ),
      }));
    }

    if (type) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter((deal) => deal.type === type),
      }));
    }

    if (region) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter(
          (deal) => deal.user && get(deal, "user.region") === region
        ),
      }));
    }

    if (cordination) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter(
          (deal) =>
            deal.user &&
            `${get(deal, "user.name")} ${get(deal, "user.lastName")}` ===
              cordination
        ),
      }));
    }

    if (principal) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter(
          (deal) =>
            deal.user &&
            `${get(deal, "user.name")} ${get(deal, "user.lastName")}` ===
              principal
        ),
      }));
    }

    if (master) {
      filtered = filtered.map((col) => ({
        ...col,
        rows: col.rows.filter(
          (deal) =>
            deal.user &&
            `${get(deal, "user.name")} ${get(deal, "user.lastName")}` === master
        ),
      }));
    }

    setColumns(filtered);
  }, [data, filters]);

  const handleDragStart = (event: DragStartEvent) => {
    const leadId = event.active.id;
    for (const col of columns) {
      const found = col.rows.find((r) => r.id === leadId);
      if (found) {
        setActiveLead(found);
        break;
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveLead(null);
    setOverColId(null);
    if (!over) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();
    if (activeId === overId) return;

    let sourceColIndex = -1;
    let targetColIndex = -1;
    let lead: Lead | undefined;

    columns.forEach((col, index) => {
      if (col.rows.find((l) => l.id === activeId)) {
        sourceColIndex = index;
        lead = col.rows.find((l) => l.id === activeId);
      }
    });

    if (columns.some((col) => col.id === overId)) {
      targetColIndex = columns.findIndex((col) => col.id === overId);
    } else if (overId.startsWith("placeholder-")) {
      const colId = overId.replace("placeholder-", "");
      targetColIndex = columns.findIndex((col) => col.id === colId);
    } else {
      columns.forEach((col, index) => {
        if (col.rows.find((l) => l.id === overId)) {
          targetColIndex = index;
        }
      });
    }

    if (lead && sourceColIndex !== -1 && targetColIndex !== -1) {
      const newColumns = [...columns];
      newColumns[sourceColIndex].rows = newColumns[sourceColIndex].rows.filter(
        (l) => l.id !== activeId
      );
      newColumns[targetColIndex].rows.unshift(lead);
      setColumns(newColumns);

      try {
        const newStatus = newColumns[targetColIndex].id;
        await updateLeadStatus(activeId, newStatus);
      } catch (error) {
        console.error("Error al actualizar el estado del lead:", error);
      }
    }
  };

  const handleDragMove = (event: any) => {
    const container = scrollRef.current;
    if (!container) return;

    const { clientX } = event.activatorEvent;
    const { left, right } = container.getBoundingClientRect();

    if (clientX - left < 80) {
      container.scrollLeft -= 15;
    } else if (right - clientX < 80) {
      container.scrollLeft += 15;
    }
  };

  const handleDragOver = (event: { over: Over | null }) => {
    if (!event.over) return;
    const overId = event.over.id.toString();
    const isCol = columns.some((col) => col.id === overId);
    if (isCol) {
      setOverColId(overId);
    } else {
      for (const col of columns) {
        if (col.rows.find((l) => l.id === overId)) {
          setOverColId(col.id);
          break;
        }
      }
    }
  };

  const handleCardFilter = (filterColor: string) => {
    setFilters({
      ...filters,
      color: filters.color !== filterColor ? filterColor : "",
    });
  };

  const toggleFilterDrawer = () => setFilterDrawerVisible((p) => !p);

  const handleFilterChange = (key: string, value: string | Date | null) => {
    setFilters({ ...filters, [key]: value });
  };

  const filtersVisibles = filterDrawerVisible || leadOpen;

  const stats = columns.flatMap((column) => {
    return column.rows.map((lead) => {
      const lastHistory = lead.history
        ? [...lead.history].sort(
            (a, b) =>
              dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
          )[0]
        : null;

      const hoursDiff = lastHistory
        ? dayjs().diff(dayjs(lastHistory.createdAt), "hour", true)
        : Infinity;

      let indicatorColor = "red";
      if (hoursDiff <= 24) indicatorColor = "green";
      else if (hoursDiff > 24 && hoursDiff <= 48) indicatorColor = "orange";

      return indicatorColor;
    });
  });

  if (isLoading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );

  return (
    <>
      <Grid size={{ xs: 12 }} sx={{ mt: 5 }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: DEFAULT_COLOR,
            mt: 2,
            display: { xs: "none", sm: "block" },
          }}
        >
          Contactos
        </Typography>
      </Grid>

      <Box display="flex" justifyContent="flex-start" sx={{ p: 2 }}>
        <Box display="flex" gap={1}>
          <Badge
            badgeContent={stats.filter((s) => s === "red").length || 0}
            max={9999}
            sx={{
              "& .MuiBadge-badge": {
                right: 8,
                top: 8,
                color: "#fff",
                backgroundColor: "#E53935",
              },
            }}
          >
            <Card
              sx={{ backgroundColor: "#E53935", color: "#fff", width: 100 }}
              onClick={() => handleCardFilter("red")}
            >
              <CardContent sx={{ py: 1, px: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {`> 48 hrs`}
                </Typography>
                <Typography variant="caption">{`Rojo: Mayor a 48 horas`}</Typography>
              </CardContent>
            </Card>
          </Badge>
          <Badge
            badgeContent={stats.filter((s) => s === "orange").length || 0}
            max={9999}
            sx={{
              "& .MuiBadge-badge": {
                right: 8,
                top: 8,
                color: "#fff",
                backgroundColor: "#FB8C00",
              },
            }}
          >
            <Card
              sx={{ backgroundColor: "#FB8C00", color: "#fff", width: 100 }}
              onClick={() => handleCardFilter("orange")}
            >
              <CardContent sx={{ py: 1, px: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  24 - 48 hrs
                </Typography>
                <Typography variant="caption">
                  Naranja: 24 a 48 horas
                </Typography>
              </CardContent>
            </Card>
          </Badge>
          <Badge
            badgeContent={stats.filter((s) => s === "green").length || 0}
            max={9999}
            sx={{
              "& .MuiBadge-badge": {
                right: 8,
                top: 8,
                color: "#fff",
                backgroundColor: "#43A047",
              },
            }}
          >
            <Card
              sx={{ backgroundColor: "#43A047", color: "#fff", width: 100 }}
              onClick={() => handleCardFilter("green")}
            >
              <CardContent sx={{ py: 1, px: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {`< 24 hrs`}
                </Typography>
                <Typography variant="caption">{`Verde: Menor a 24 horas`}</Typography>
              </CardContent>
            </Card>
          </Badge>
        </Box>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragMove={handleDragMove}
        onDragOver={handleDragOver}
      >
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            overflowX: "auto",
            gap: 2,
            p: 2,
            WebkitOverflowScrolling: "touch",
            touchAction: "none",
            userSelect: "none",
          }}
        >
          {columns.map((col) => (
            <DroppableColumn
              key={col.id}
              column={col}
              isOver={overColId === col.id}
            >
              <SortableContext
                items={
                  col.rows.length > 0
                    ? col.rows.map((l) => l.id)
                    : [`placeholder-${col.id}`]
                }
                strategy={verticalListSortingStrategy}
              >
                {col.rows.length > 0 ? (
                  col.rows.map((lead) => <LeadCard key={lead.id} lead={lead} />)
                ) : (
                  <EmptyPlaceholder id={`placeholder-${col.id}`} />
                )}
              </SortableContext>
            </DroppableColumn>
          ))}
        </Box>

        <DragOverlay dropAnimation={null}>
          {activeLead ? <DragOverlayCard lead={activeLead} /> : null}
        </DragOverlay>
      </DndContext>

      <Fab
        aria-label="add"
        sx={{
          backgroundColor: ACTIVE_COLOR,
          color: "white",
          position: "fixed",
          bottom: { xs: 220, sm: 220 },
          right: 16,
          zIndex: 3000,
          "&:hover": { backgroundColor: ACTIVE_COLOR },
          display: filtersVisibles ? "none" : "",
        }}
        onClick={() => setLeadOpen(true)}
      >
        <Plus />
      </Fab>

      <Fab
        aria-label="filter"
        sx={{
          backgroundColor: YELLOW_COLOR,
          color: "white",
          position: "fixed",
          bottom: { xs: 150, sm: 150 },
          right: 16,
          zIndex: 3000,
          "&:hover": { backgroundColor: YELLOW_COLOR },
          display: filtersVisibles ? "none" : "",
        }}
        onClick={toggleFilterDrawer}
      >
        <Badge
          badgeContent={Object.keys(filters).length}
          invisible={Object.keys(filters).length === 0}
          overlap="circular"
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: YELLOW_COLOR, // 👈 tu color
              color: "white", // texto negro
              fontSize: "0.75rem", // tamaño de texto
              minWidth: 20,
              height: 20,
              padding: "0 4px",
              border: "2px solid white", // separa visualmente del FAB
            },
          }}
        >
          <FilterIcon style={{ fontSize: 28 }} />{" "}
        </Badge>
      </Fab>
      <Fab
        aria-label="bot"
        sx={{
          backgroundColor: DEFAULT_COLOR,
          color: "white",
          position: "fixed",
          bottom: { xs: 80, sm: 80 },
          right: 16,
          zIndex: 3000,
          display: filtersVisibles ? "none" : "",
          "&:hover": { backgroundColor: DEFAULT_COLOR },
        }}
        onClick={() => window.open(BOT_URL, "_blank")}
      >
        <Bot />
      </Fab>

      <FilterDrawer
        open={filterDrawerVisible}
        onClose={toggleFilterDrawer}
        filterChange={handleFilterChange}
        handleResetFilters={resetFilters}
        filters={filters}
      />

      <LeadDrawer
        open={leadOpen}
        onClose={() => {
          setSelectedLead(null);
          setLeadOpen(false);
        }}
        lead={selectedLead}
      />
    </>
  );
}
